import {
  computed,
  effect,
  inject,
  Injectable,
  InjectionToken,
  Injector,
  linkedSignal,
  Provider,
  Resource,
  signal,
  Signal,
  Type,
  untracked,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataViewPageProvider } from '@app/admin/providers/data-view-page.provider';
import { IndexExportFormat } from '@libs/shared/domain/aggregations/index/index-export-format.schema';
import { DialogService } from '@services/dialog.service';
import { UserService } from '@services/user.service';
import { ExportResultModalComponent } from '@shared/components/export-result-modal/export-result-modal.component';
import { DataViewDefinitionService, DataViewDefinitions } from '@shared/dataview/config/dataview-definition.service';
import { DataViewUserViewsService } from '@shared/dataview/config/dataview-views.service';
import {
  DataViewState,
  DataViewUserViewsSchema,
  DataViewViews,
  FilterQueryInput,
  MapConfig,
  ViewPreset,
} from '@shared/dataview/core/dataview-types';
import { DataViewColumnsStore } from '@shared/dataview/state/columns.store';
import { createDataViewData, DataViewData, DEFAULT_PAGE_SIZE } from '@shared/dataview/state/data';
import { DataViewDraftStorage } from '@shared/dataview/state/draft-storage';
import { DataViewFilterStore } from '@shared/dataview/state/filter.store';
import { DefaultViewOverwriteError, ViewPresetStore } from '@shared/dataview/state/view-preset.store';
import { OpenSearchDomainService } from '@shared/models/open-search-domain-service';
import { OpenSearchSimpleSort } from '@shared/providers/opensearch-api';
import { catchError, concatMap, finalize, map, Observable, of, Subject } from 'rxjs';

export interface DataViewPageDef<T> {
  pageName: string;
  search: Type<OpenSearchDomainService<T>>;
  defaultSort?: OpenSearchSimpleSort;
  pageSize?: number;
  mapItems?: (items: T[]) => T[];
  gate?: (injector: Injector) => Observable<boolean>;
  implicitFilters?: (injector: Injector) => Signal<FilterQueryInput[]>;
  projection?: { mode: 'columns' | 'all'; alwaysFields?: string[] };
  exportColumnSet?: string;
}

export type SaveState = { status: 'idle' } | { status: 'saving' } | { status: 'saved' } | { status: 'error'; error: unknown };

type ExportError = { error?: { message?: string; errors?: string[] } };

const EMPTY_DEFINITIONS: DataViewDefinitions = { columns: [], filters: [], categories: [] };
const EMPTY_USER_VIEWS: DataViewViews = { views: [], favouriteColumnIds: [] };

export const DATA_VIEW_PAGE_DEF = new InjectionToken<DataViewPageDef<unknown>>('DATA_VIEW_PAGE_DEF');

@Injectable()
export class DataViewPageStore<T> {
  private readonly dataViewPageProvider = inject(DataViewPageProvider);
  private readonly definitionService = inject(DataViewDefinitionService);
  private readonly dialogService = inject(DialogService);
  private readonly draftStorage = inject(DataViewDraftStorage);
  private readonly injector = inject(Injector);
  private readonly pageDef = inject(DATA_VIEW_PAGE_DEF) as DataViewPageDef<T>;
  private readonly userService = inject(UserService);
  private readonly userViewsService = inject(DataViewUserViewsService);

  private readonly searchService = this.injector.get(this.pageDef.search);
  private readonly configReloadTick = signal(0);

  private readonly definitionsResource = rxResource({
    defaultValue: EMPTY_DEFINITIONS,
    params: () => ({ page: this.pageDef.pageName, tick: this.configReloadTick() }),
    stream: ({ params }) => this.definitionService.load(params.page),
  });

  private readonly userViewsResource = rxResource({
    defaultValue: EMPTY_USER_VIEWS,
    params: () => ({ page: this.pageDef.pageName, tick: this.configReloadTick() }),
    stream: ({ params }) => this.userViewsService.load(params.page),
  });

  private readonly definitions = computed(() => valueOr(this.definitionsResource, EMPTY_DEFINITIONS));
  private readonly userViews = computed(() => valueOr(this.userViewsResource, EMPTY_USER_VIEWS));
  private readonly configReady = computed(() => isSettled(this.definitionsResource) && isSettled(this.userViewsResource));
  private readonly ready = signal(false);

  readonly isReady = this.ready.asReadonly();

  readonly filters = new DataViewFilterStore({
    definitions: computed(() => this.definitions().filters),
    implicit: this.pageDef.implicitFilters?.(this.injector),
  });

  readonly columns = new DataViewColumnsStore({
    available: computed(() => this.definitions().columns),
    projection: this.pageDef.projection?.mode ?? 'all',
    alwaysFields: this.pageDef.projection?.alwaysFields,
    favouriteIds: computed(() => this.userViews().favouriteColumnIds),
  });

  private readonly mergedViews = linkedSignal<Array<ViewPreset<DataViewState>> | undefined, Array<ViewPreset<DataViewState>>>({
    source: () => (this.configReady() ? this.userViews().views : undefined),
    computation: (views, previous) => views ?? previous?.value ?? [],
  });

  readonly views = new ViewPresetStore<DataViewState>({
    pageName: this.pageDef.pageName,
    storage: this.draftStorage,
    views: this.mergedViews.asReadonly(),
    normalize: state => this.canonicalState(state),
  });

  readonly pageSize = this.pageDef.pageSize ?? DEFAULT_PAGE_SIZE;
  readonly sort = signal<OpenSearchSimpleSort>(this.pageDef.defaultSort ?? {});
  readonly mapConfig = signal<MapConfig | undefined>(undefined);

  readonly data: DataViewData<T> = createDataViewData<T>({
    search: (filter, page, sort, fields) => this.searchService.searchData(filter, page, sort, fields),
    count: filter => this.searchService.countData(filter),
    requestFilters: this.filters.requestFilters,
    sort: this.sort,
    fields: this.columns.fields,
    pageSize: signal(this.pageSize),
    gate: this.pageDef.gate ? () => this.pageDef.gate!(this.injector) : undefined,
    mapItems: this.pageDef.mapItems,
    enabled: this.ready,
  });

  readonly isExporting = signal(false);
  readonly isLoadingConfig = computed(() => this.definitionsResource.isLoading() || this.userViewsResource.isLoading());
  readonly categories = computed(() => this.definitions().categories);

  readonly configError = computed(() => {
    if (this.definitionsResource.status() === 'error') return { source: 'definitions' as const, error: this.definitionsResource.error() };
    if (this.userViewsResource.status() === 'error') return { source: 'userViews' as const, error: this.userViewsResource.error() };
    return undefined;
  });

  readonly canSaveViews = computed(() => isSettled(this.userViewsResource));
  readonly canSaveCurrent = computed(() => this.canSaveViews() && this.views.canSaveCurrent());
  readonly saveState = signal<SaveState>({ status: 'idle' });

  private readonly saveQueue = new Subject<DataViewUserViewsSchema>();
  private pendingSaves = 0;

  constructor() {
    this.syncStoresOnViewSelection();
    this.syncDraftOnEdit();
    this.processSaves();

    this.dataViewPageProvider.exportTrigger.pipe(takeUntilDestroyed()).subscribe(allColumns => this.export('xlsx', allColumns));
  }

  reloadConfig(): void {
    this.configReloadTick.update(tick => tick + 1);
  }

  export(format: IndexExportFormat = 'xlsx', allColumns = false): void {
    if (this.isExporting()) return;
    this.isExporting.set(true);

    const fields = allColumns ? undefined : this.columns.exportFields();
    const columnSet = this.pageDef.exportColumnSet ?? `${this.pageDef.pageName}_columns`;

    this.searchService
      .exportData(this.filters.requestFilters(), this.sort(), fields, format, columnSet)
      .pipe(
        finalize(() => {
          this.isExporting.set(false);
          this.dataViewPageProvider.exportFinished.next();
        }),
      )
      .subscribe({
        next: data => {
          this.openExportResult().data = data;
        },
        error: (error: ExportError) => {
          const result = this.openExportResult();
          result.exportFailed = true;
          if (error?.error?.message) result.errorMessage = error.error.message;
          if (Array.isArray(error?.error?.errors)) result.errorDetails = error.error.errors;
        },
      });
  }

  saveCurrentView(label?: string): boolean {
    if (!this.canSaveViews()) return false;

    try {
      if (!this.views.saveCurrent(label)) return false;
    } catch (error) {
      if (!(error instanceof DefaultViewOverwriteError)) throw error;
      this.saveState.set({ status: 'error', error });
      return false;
    }

    this.persistUserViews();
    return true;
  }

  saveViewAs(options: { id: string; label: string }): boolean {
    if (!this.canSaveViews()) return false;
    if (!this.views.saveAsNew({ ...options, ownerUsername: this.userService.getCurrentUser().username })) return false;
    this.persistUserViews();
    return true;
  }

  retrySave(): void {
    if (this.canSaveViews()) this.persistUserViews();
  }

  savePreferences(): void {
    if (this.canSaveViews()) this.persistUserViews();
  }

  discardChanges(): void {
    this.views.resetDraft();
    const state = this.views.draft();
    if (state) this.applyViewState(state);
  }

  private openExportResult(): ExportResultModalComponent {
    return this.dialogService.openModal(ExportResultModalComponent, { centered: false, size: 'sm' }).componentInstance;
  }

  private persistUserViews(): void {
    this.saveQueue.next(
      this.userViewsService.buildSaveable({
        views: this.views.views(),
        favouriteColumnIds: this.columns.favouriteIds(),
      }),
    );
  }

  private processSaves(): void {
    this.saveQueue
      .pipe(
        map(config => {
          this.pendingSaves += 1;
          this.saveState.set({ status: 'saving' });
          return config;
        }),
        concatMap(config =>
          this.userViewsService.save(this.pageDef.pageName, this.userService.getCurrentUser(), config).pipe(
            map((): SaveState => ({ status: 'saved' })),
            catchError(error => of<SaveState>({ status: 'error', error })),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(result => {
        this.pendingSaves -= 1;
        if (result.status === 'error' || this.pendingSaves === 0) this.saveState.set(result);
      });
  }

  private applyViewState(state: DataViewState): void {
    this.filters.applyFromSchema(state.filters);
    this.columns.applyFromSchema(state.columns);
    this.mapConfig.set(state.mapConfig);
  }

  private canonicalState(state: DataViewState): DataViewState {
    return {
      filters: this.filters.canonicalize(state.filters),
      columns: this.columns.canonicalize(state.columns),
      ...(state.mapConfig === undefined ? {} : { mapConfig: state.mapConfig }),
    };
  }

  private syncStoresOnViewSelection(): void {
    effect(() => {
      if (!this.configReady()) return;
      const viewId = this.views.currentId();

      // The draft is read untracked, or this would re-run on the edits syncDraftOnEdit writes back.
      untracked(() => {
        const state = viewId ? this.views.draft() : undefined;
        if (state) this.applyViewState(state);
        this.ready.set(true);
      });
    });
  }

  private syncDraftOnEdit(): void {
    effect(() => {
      const mapConfig = this.mapConfig();
      const state: DataViewState = {
        filters: this.filters.toSchema(),
        columns: this.columns.toSchema(),
        ...(mapConfig === undefined ? {} : { mapConfig }),
      };

      untracked(() => this.views.setDraft(state));
    });
  }
}

export function provideDataView<T>(pageDef: DataViewPageDef<T>): Provider[] {
  return [{ provide: DATA_VIEW_PAGE_DEF, useValue: pageDef }, DataViewPageStore];
}

function valueOr<T>(resource: Resource<T>, fallback: T): T {
  return resource.status() === 'error' ? fallback : resource.value();
}

function isSettled(resource: Resource<unknown>): boolean {
  const status = resource.status();
  return status === 'resolved' || status === 'local';
}
